import axios from "axios";
import { AccountHolder, Cache } from "../../Account";
import { AbstractBaseResponse } from "../../ForgotPINDialog";
import { ListSortType, SearchSortType } from "./lists/SavedListSortControl";
import { ResourceDWR } from "../../../dto/SuggestionResult";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";
import { GlobalConfigHolder } from "../../GlobalConfig";
import { SupportedFunctionalityHolder } from "../../SupportedFunctionality";

export enum ResourceListSortType {
  Name = "Name",
  CreationDate = "CreationDate",
  LastUpdateDate = "LastUpdateDate",
}
export interface EditGuardianProhibitedListResponse
  extends AbstractBaseResponse {}

export enum ResourceListOwnerType {
  User = "User",
  Configuration = "Configuration",
}

export enum ResourceListScope {
  Public = "Public",
  Private = "Private",
}

interface AddRecordsToGuardianProhibitedListResponse
  extends AbstractBaseResponse {
  totalEResourcesNotAdded: number;
}
interface StaticResourceListView {
  id: number;
  creatorName: string;
  ownerType: ResourceListOwnerType;
  ownerId: number;
  scope: ResourceListScope;
  name: string;
  caption: string;
  creationDate: string; // Date
  lastUpdateDate: string; // Date
  lastFeedRetrievalDate: string; // Date
  resourceCount: number;
  url: string; // external resource list field
}

interface GuardianProhibitedListDwr {
  listId: number;
  listName: string;
  childIds: number[];
  records: SearchResultDwr;
}

interface StaticResourceListParameters {
  name: string;
  scope: ResourceListScope;
  ownerType?: ResourceListOwnerType;
  resourceIds: number[];
}

export interface StaticResourceListSummary {
  id: number;
  creatorName: string;
  ownerType: ResourceListOwnerType;
  ownerId: number;
  scope: ResourceListScope;
  name: string;
  caption: string;
  creationDate: Date;
  lastUpdateDate: Date;
  lastFeedRetrievalDate: Date;
  resourceCount: number;
}

export enum ListType {
  Prohibited = "Prohibited",
  Config = "Config",
  Personal = "Personal",
}

export interface AllListsSummary {
  id: number;
  name: string;
  type: ListType;
  pathName: string;
  count: number;
}

export enum ResourceListModificationStatus {
  Allowed = "Allowed",
  Blocked_NoUserInContext = "Blocked_NoUserInContext",
  Blocked_ListDoesNotExist = "Blocked_ListDoesNotExist",
  Blocked_ListNotOwnedByUser = "Blocked_ListNotOwnedByUser",
  Blocked_UserDoesNotManageConfiguration = "Blocked_UserDoesNotManageConfiguration",
  Blocked_BackendException = "Blocked_BackendException",
}

export interface ResourceListModificationReturn {
  modificationStatus: ResourceListModificationStatus;
  success: boolean;
  resourceListName: string;
  resourceListId: number;
}

interface GuardianProhibitedListSummary {
  id: number;
  name: string;
  totalRecords: number;
}

interface GetGuardianProhibitedListSummariesResponse
  extends AbstractBaseResponse {
  listSummaries: GuardianProhibitedListSummary[];
}
interface DeleteRecordsFromGuardianProhibitedListResponse
  extends AbstractBaseResponse {}

export interface DeleteListReturn {
  modificationStatus: ResourceListModificationStatus;
  success: boolean;
}
interface DeleteGuardianProhibitedListResponse extends AbstractBaseResponse {}
export interface SearchResultDwr {
  totalHits: number;
  resources: ResourceDWR[];
}

interface AddGuardianProhibitedListResponse extends AbstractBaseResponse {
  listId: number;
}

export const ListService = (() => {
  class ListHolder {
    // These cached responses are requested in loaders
    private readonly personalLists = new Cache<StaticResourceListSummary[]>(
      null
    );
    private readonly configLists = new Cache<StaticResourceListSummary[]>(null);
    private readonly prohibitedLists = new Cache<
      GuardianProhibitedListSummary[]
    >(null);

    private pendingGetPersonalLists: Promise<
      readonly StaticResourceListSummary[]
    >;
    private async getPersonalLists() {
      if (this.pendingGetPersonalLists) {
        return this.pendingGetPersonalLists;
      }

      if (!this.personalLists.valid) {
        this.pendingGetPersonalLists = (async () => {
          const firstResult = 0;
          const maxResults = 1000000;
          const sortType = ResourceListSortType.Name;

          const { data } = await axios.get<StaticResourceListSummary[]>(
            `/lists/static/${firstResult}/${maxResults}/${sortType}`
          );
          this.personalLists.set(data);
          this.pendingGetPersonalLists = null;
          return this.personalLists.current;
        })();

        return this.pendingGetPersonalLists;
      }

      return this.personalLists.current;
    }

    private pendingGetConfigLists: Promise<
      readonly StaticResourceListSummary[]
    >;
    private async getConfigLists() {
      if (this.pendingGetConfigLists) {
        return this.pendingGetConfigLists;
      }

      if (!this.configLists.valid) {
        this.pendingGetConfigLists = (async () => {
          const firstResult = 0;
          const maxResults = 1000000;
          const sortType = ResourceListSortType.Name;
          const { data } = await axios.get<StaticResourceListSummary[]>(
            `/lists/static/config/${firstResult}/${maxResults}/${sortType}`
          );
          this.configLists.set(data);
          this.pendingGetConfigLists = null;
          return this.configLists.current;
        })();

        return this.pendingGetConfigLists;
      }

      return this.configLists.current;
    }

    private pendingGetProhibitedLists: Promise<
      readonly GuardianProhibitedListSummary[]
    >;
    private async getProhibitedLists() {
      if (this.pendingGetProhibitedLists) {
        return this.pendingGetProhibitedLists;
      }

      if (!this.prohibitedLists.valid) {
        this.pendingGetProhibitedLists = (async () => {
          const { data } =
            await axios.get<GetGuardianProhibitedListSummariesResponse>(
              `/list/prohibited-list/`
            );
          this.prohibitedLists.set(data.listSummaries);
          this.pendingGetProhibitedLists = null;
          return this.prohibitedLists.current;
        })();
        return this.pendingGetProhibitedLists;
      }

      return this.prohibitedLists.current;
    }

    private async removeResourceIds(listId: number, resourceIds: number[]) {
      const { data } = await axios.post<ResourceListModificationReturn>(
        `/list/static/${listId}/remove`,
        resourceIds
      );
      return data;
    }

    private async deleteProhibitedListRecords(
      listId: number,
      resourceIds: number[]
    ) {
      const { data } =
        await axios.post<DeleteRecordsFromGuardianProhibitedListResponse>(
          `/list/prohibited-list/${listId}/records/delete`,
          resourceIds
        );
      return data;
    }

    private async getProhibitedList(
      listId: number,
      page: number = 0,
      pageSize: number = 10,
      sortType: SearchSortType = SearchSortType.Title
    ) {
      const firstResult = page * pageSize;
      const { data } = await axios.get<GuardianProhibitedListDwr>(
        `/list/prohibited-list/${listId}/${firstResult}/${pageSize}/${sortType}`
      );
      return data;
    }

    private async getStaticResourceListResources(
      listId: number,
      page: number = 0,
      pageSize: number = 10,
      sortType: ListSortType = ListSortType.Title
    ) {
      const firstResult = page * pageSize;
      // if it's null don't request it
      return (
        await axios.get<SearchResultDwr>(
          `/list/static/${listId}/${firstResult}/${pageSize}/${sortType}`
        )
      ).data;
    }

    private async deleteStaticResourceList(listId: number) {
      const { data } = await axios.post<DeleteListReturn[]>(`lists/delete`, {
        listIds: [listId],
      });
      const { success } = data[0];
      return success;
    }

    private async deleteProhibitedList(listId: number) {
      const { data } = await axios.delete<DeleteGuardianProhibitedListResponse>(
        `/list/prohibited-list/${listId}`
      );
      return data;
    }

    private async renameProhibitedList(listId: number, listName: string) {
      const { data } = await axios.put<EditGuardianProhibitedListResponse>(
        `/list/prohibited-list/${listId}`,
        listName,
        {
          headers: {
            "Content-Type": "text/plain", // Set content type to plain text if required
          },
        }
      );
      return data;
    }

    private async renameStaticResourceList(listId: number, listName: string) {
      const { data } = await axios.get<ResourceListModificationReturn>(
        `/list/${listId}/${listName}`
      );
      return data;
    }

    private async createProhibitedList(
      listName: string,
      childIds: BorrowerChild["id"]
    ) {
      const { data } = await axios.post<AddGuardianProhibitedListResponse>(
        `/list/prohibited-list`,
        {
          listName,
          childIds,
        }
      );
      return data;
    }

    private async createStaticResourceList(
      name: string,
      listType: ListType,
      resourceIds: number[]
    ) {
      // TODO check scope for Config Admin lists, there may be an option to make private
      const payload: StaticResourceListParameters = {
        name,
        resourceIds,
        scope:
          listType === ListType.Config
            ? ResourceListScope.Public
            : ResourceListScope.Private,
      };
      const url =
        listType === ListType.Config ? `/list/static/config` : `/list/static`;
      const { data } = await axios.post<StaticResourceListView>(url, payload);
      return data.id;
    }

    private async addToProhibitedList(
      listId: number,
      resourceIds: ResourceDWR["id"][]
    ) {
      const { data } =
        await axios.post<AddRecordsToGuardianProhibitedListResponse>(
          `/list/prohibited-list/${listId}/records/add`,
          resourceIds
        );
      return data;
    }

    private async addToStaticList(
      listId: number,
      resourceIds: ResourceDWR["id"][]
    ) {
      const { data } = await axios.post<ResourceListModificationReturn>(
        `/list/static/${listId}/add`,
        resourceIds
      );
      return data;
    }

    public async renameList(
      listId: number,
      listType: ListType,
      listName: string
    ) {
      if (listType === ListType.Prohibited) {
        const { status: success } = await this.renameProhibitedList(
          listId,
          listName
        );
        if (success) {
          this.prohibitedLists.invalidate();
        }
        return success;
      }
      const { success } = await this.renameStaticResourceList(listId, listName);
      if (success) {
        if (listType === ListType.Config) {
          this.configLists.invalidate();
        }
        if (listType === ListType.Personal) {
          this.personalLists.invalidate();
        }
      }
      return success;
    }

    public async deleteList(listId: number, listType: ListType) {
      if (listType === ListType.Prohibited) {
        const { status: success } = await this.deleteProhibitedList(listId);
        if (success) {
          this.prohibitedLists.invalidate();
          AccountHolder.fetchAccountSummary(true); // update list counts
        }
        return success;
      }
      const success = await this.deleteStaticResourceList(listId);
      if (success) {
        if (listType === ListType.Personal) {
          this.personalLists.invalidate();
        }
        if (listType === ListType.Config) {
          this.configLists.invalidate();
        }
        AccountHolder.fetchAccountSummary(true); // update list counts
      }
      return success;
    }

    public async removeListItems(
      listId: number,
      listType: ListType,
      resourceIds: number[]
    ) {
      if (listType === ListType.Prohibited) {
        const {
          status: { success },
        } = await this.deleteProhibitedListRecords(listId, resourceIds);

        if (success) {
          this.prohibitedLists.invalidate();
        }
        return success;
      }

      const { success } = await this.removeResourceIds(listId, resourceIds);
      if (success) {
        if (listType === ListType.Config) {
          this.configLists.invalidate();
        }
        if (listType === ListType.Personal) {
          this.personalLists.invalidate();
        }
      }

      return success;
    }

    public async getListResources(
      listId: number,
      listType: ListType,
      page: number = 0,
      pageSize: number = 10,
      sortType: ListSortType | SearchSortType
    ) {
      if (listType === ListType.Prohibited) {
        const {
          childIds,
          records: { totalHits, resources },
        } = await this.getProhibitedList(
          listId,
          page,
          pageSize,
          sortType as SearchSortType
        );
        return { totalHits, resources, childIds };
      }

      const { totalHits, resources } =
        await this.getStaticResourceListResources(
          listId,
          page,
          pageSize,
          sortType as ListSortType
        );
      return { totalHits, resources };
    }

    public async getAllLists() {
      const personalLists = await this.getPersonalLists();
      await AccountHolder.fetchAccountData(); // prefetch account info
      const configLists = AccountHolder.isConfigAdmin
        ? await this.getConfigLists()
        : [];
      console.log("AccountHolder.hasChildren", AccountHolder.hasChildren);
      const { useGuardianProhibition } =
        await SupportedFunctionalityHolder.getSupportedFunctionality();
      const prohibitedLists =
        useGuardianProhibition && AccountHolder.hasChildren
          ? await this.getProhibitedLists()
          : [];
      console.log("prohibitedLists", prohibitedLists);
      // Default priority is Prohibited, Config, then Personal
      const allLists: AllListsSummary[] = [
        ...(prohibitedLists || []).map((list) => ({
          id: list.id,
          name: list.name,
          type: ListType.Prohibited,
          pathName: `/myaccount/lists/${list.id}`,
          count: list.totalRecords,
        })),
        ...(configLists || []).map((list) => ({
          id: list.id,
          name: list.name,
          type: ListType.Config,
          pathName: `/myaccount/lists/${list.id}`,
          count: list.resourceCount,
        })),
        ...personalLists.map((list) => ({
          id: list.id,
          name: list.name,
          type: ListType.Personal,
          pathName: `/myaccount/lists/${list.id}`,
          count: list.resourceCount,
        })),
      ];

      return {
        allLists,
      };
    }

    public async getDefaultListPath() {
      const { allLists } = await this.getAllLists();
      const notNullLists = allLists.filter((list) => list.id !== null);
      if (notNullLists.length > 0) {
        const { pathName } = notNullLists[0];
        return pathName;
      } else {
        // default empty "My List"
        return `/myaccount/lists/null`;
      }
    }

    public async createList(
      listName: string,
      listType: ListType,
      resourceIds: number[],
      childIds: BorrowerChild["id"]
    ) {
      // TODO refetch account summary list count
      if (listType === ListType.Prohibited) {
        const {
          status: { success },
          listId,
        } = await this.createProhibitedList(listName, childIds);
        if (success) {
          AccountHolder.fetchAccountSummary(true); // update list counts
          this.prohibitedLists.invalidate();
          const response = await this.addToList(listId, listType, resourceIds);
          return {
            ...response,
            listId,
          };
        } else {
          throw new Error("Error creating list");
        }
      }

      try {
        const listId = await this.createStaticResourceList(
          listName,
          listType,
          resourceIds
        );
        AccountHolder.fetchAccountSummary(true); // update list counts
        if (listType === ListType.Config) {
          this.configLists.invalidate();
        }
        if (listType === ListType.Personal) {
          this.personalLists.invalidate();
        }

        return {
          success: true,
          totalEResourcesNotAdded: 0,
          successCount: resourceIds.length,
          listId,
        };
      } catch (e) {
        console.error(e);
        throw new Error(e);
      }
    }

    public async addToList(
      listId: number,
      listType: ListType,
      resourceIds: ResourceDWR["id"][]
    ) {
      if (listType === ListType.Prohibited) {
        const {
          status: { success },
          totalEResourcesNotAdded,
        } = await this.addToProhibitedList(listId, resourceIds);
        if (success) {
          this.prohibitedLists.invalidate();
        }
        return {
          success,
          totalEResourcesNotAdded,
          successCount: resourceIds.length - totalEResourcesNotAdded,
        };
      }

      const { success } = await this.addToStaticList(listId, resourceIds);
      if (success) {
        if (listType === ListType.Config) {
          this.configLists.invalidate();
        }
        if (listType === ListType.Personal) {
          this.personalLists.invalidate();
        }
      }
      return {
        success,
        totalEResourcesNotAdded: 0,
        successCount: resourceIds.length,
      };
    }

    public clearListData() {
      this.personalLists.set(null);
      this.personalLists.invalidate();
      this.prohibitedLists.set(null);
      this.prohibitedLists.invalidate();
      this.configLists.set(null);
      this.configLists.invalidate();
    }

    // TODO add a subscribe function?
  }

  return new ListHolder();
})();
